import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Check, CreditCard, Calendar as CalendarIcon, Clock, User, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const sessionTypes = [
  { value: 'portrait', label: 'Portrait Session' },
  { value: 'wedding', label: 'Wedding Photography' },
  { value: 'event', label: 'Event Coverage' },
  { value: 'graduation', label: 'Graduation Photos' },
  { value: 'studio', label: 'Studio Session' },
];

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    session_type: ''
  });

  // Check payment status if returning from Stripe
  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const checkPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    setCheckingPayment(true);
    
    try {
      const { data } = await axios.get(`${API}/api/payments/status/${sid}`);
      
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Your booking is confirmed.');
        navigate('/booking/success?confirmed=true');
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
      } else {
        setTimeout(() => checkPaymentStatus(sid, attempts + 1), pollInterval);
      }
    } catch (e) {
      console.error('Error checking payment:', e);
      toast.error('Error verifying payment. Please contact support.');
      setCheckingPayment(false);
    }
  };

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const { data } = await axios.get(`${API}/api/booking/slots?date=${dateStr}`);
      setAvailableSlots(data.available_slots);
      setBookedSlots(data.booked_slots);
      setSelectedTime('');
    } catch (e) {
      console.error('Error fetching slots:', e);
      toast.error('Failed to load available times');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !formData.name || !formData.email || !formData.phone || !formData.session_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      // Create booking
      const bookingData = {
        ...formData,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime
      };
      
      const { data: booking } = await axios.post(`${API}/api/bookings`, bookingData);
      
      // Create Stripe checkout session
      const { data: checkout } = await axios.post(`${API}/api/payments/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = checkout.url;
    } catch (e) {
      console.error('Booking error:', e);
      toast.error(e.response?.data?.detail || 'Failed to create booking');
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-6"></div>
          <h2 className="font-['Playfair_Display'] text-2xl text-white mb-2">Processing Payment</h2>
          <p className="text-gray-400">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="booking-page">
      {/* Header */}
      <section className="py-12 text-center">
        <div className="container-luxury">
          <p className="text-[#C9A14A] uppercase tracking-[0.2em] text-sm mb-4">Schedule</p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6">
            Book Your Session
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Select your preferred date and time, and secure your booking with a $20 deposit.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="pb-24">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            
            {/* Left: Calendar */}
            <div className="card-luxury p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <CalendarIcon className="w-5 h-5 text-[#C9A14A]" />
                <h3 className="font-['Playfair_Display'] text-xl text-white">Select Date & Time</h3>
              </div>
              
              <div className="flex justify-center mb-8">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={isDateDisabled}
                  className="rounded-none border border-[#333] bg-[#1A1A1A] text-white"
                  data-testid="booking-calendar"
                />
              </div>

              {selectedDate && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#C9A14A]" />
                    <h4 className="text-white">Available Times</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {availableSlots.length > 0 ? (
                      availableSlots.map((slot) => (
                        <Button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          className={`${
                            selectedTime === slot
                              ? 'btn-gold'
                              : 'bg-[#222] text-white border border-[#333] hover:border-[#C9A14A]'
                          } py-3 text-sm`}
                          data-testid={`time-slot-${slot}`}
                        >
                          {slot}
                        </Button>
                      ))
                    ) : (
                      <p className="col-span-4 text-gray-500 text-center py-4">
                        No available slots for this date
                      </p>
                    )}
                  </div>
                  
                  {bookedSlots.length > 0 && (
                    <p className="text-gray-500 text-xs">
                      Booked slots: {bookedSlots.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Right: Form */}
            <div className="card-luxury p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-5 h-5 text-[#C9A14A]" />
                <h3 className="font-['Playfair_Display'] text-xl text-white">Your Details</h3>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-400 text-sm">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className="input-luxury"
                    required
                    data-testid="booking-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-400 text-sm">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="input-luxury"
                    required
                    data-testid="booking-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-400 text-sm">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    className="input-luxury"
                    required
                    data-testid="booking-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-400 text-sm">Session Type</Label>
                  <Select
                    value={formData.session_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, session_type: value }))}
                  >
                    <SelectTrigger className="bg-transparent border-b border-[#333] focus:border-[#C9A14A] rounded-none text-white" data-testid="booking-session-type">
                      <SelectValue placeholder="Select session type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#333]">
                      {sessionTypes.map((type) => (
                        <SelectItem 
                          key={type.value} 
                          value={type.value}
                          className="text-white hover:bg-[#333] focus:bg-[#333]"
                        >
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Summary */}
                {selectedDate && selectedTime && (
                  <div className="p-4 bg-[#222] border border-[#333]">
                    <h4 className="text-white text-sm mb-3">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Date:</span>
                        <span className="text-white">{selectedDate.toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Time:</span>
                        <span className="text-white">{selectedTime}</span>
                      </div>
                      {formData.session_type && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Session:</span>
                          <span className="text-white">
                            {sessionTypes.find(t => t.value === formData.session_type)?.label}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-[#333]">
                        <span className="text-gray-400">Deposit:</span>
                        <span className="text-[#C9A14A] font-semibold">$20.00</span>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full btn-gold flex items-center justify-center gap-2"
                  disabled={loading || !selectedDate || !selectedTime}
                  data-testid="booking-submit-button"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Pay $20 Deposit
                    </>
                  )}
                </Button>

                <p className="text-gray-500 text-xs text-center">
                  Secure payment powered by Stripe. Your deposit will be applied to your final session cost.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
